import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Building2, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  User,
  ArrowLeft,
  MoreVertical,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, supplier: null, stats: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async (search = "") => {
    try {
      const response = await axios.get(`${API}/purchasing/suppliers`, {
        params: { search }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Kunde inte hämta leverantörer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuppliers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openDeleteDialog = async (supplier) => {
    try {
      // Fetch stats for this supplier
      const response = await axios.get(`${API}/purchasing/suppliers/${supplier.id}/stats`);
      setDeleteDialog({ open: true, supplier, stats: response.data });
    } catch (error) {
      // If stats fail, still open dialog with null stats
      setDeleteDialog({ open: true, supplier, stats: { prices_count: 0, orders_count: 0 } });
    }
  };

  const handleDelete = async (force = false) => {
    if (!deleteDialog.supplier) return;
    
    setDeleting(true);
    try {
      const endpoint = force 
        ? `${API}/purchasing/suppliers/${deleteDialog.supplier.id}/force`
        : `${API}/purchasing/suppliers/${deleteDialog.supplier.id}`;
      
      const response = await axios.delete(endpoint);
      toast.success(response.data.message || "Leverantör borttagen");
      setDeleteDialog({ open: false, supplier: null, stats: null });
      fetchSuppliers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort leverantör");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/purchasing">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-500" />
                  <h1 className="font-heading text-xl font-bold uppercase tracking-tight" data-testid="suppliers-title">
                    Leverantörer
                  </h1>
                </div>
                <p className="text-sm text-muted-foreground">
                  {suppliers.length} leverantörer
                </p>
              </div>
            </div>
            <Link to="/purchasing/suppliers/new">
              <Button className="gap-2" data-testid="add-supplier-btn">
                <Plus className="h-4 w-4" />
                Ny leverantör
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Sök leverantör..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="supplier-search"
          />
        </div>

        {/* Suppliers List */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Laddar...</div>
        ) : suppliers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">Inga leverantörer hittades</p>
              <Link to="/purchasing/suppliers/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Lägg till leverantör
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {suppliers.map((supplier) => (
              <Card 
                key={supplier.id} 
                className="hover:border-primary/50 transition-colors"
                data-testid={`supplier-${supplier.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/purchasing/suppliers/${supplier.id}`)}
                    >
                      <h3 className="font-heading font-bold text-lg uppercase">
                        {supplier.company_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                        {supplier.contact_person && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {supplier.contact_person}
                          </span>
                        )}
                        {supplier.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {supplier.email}
                          </span>
                        )}
                        {supplier.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {supplier.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/purchasing/suppliers/${supplier.id}`)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Redigera
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/purchasing/suppliers/${supplier.id}/prices`)}>
                          <Building2 className="h-4 w-4 mr-2" />
                          Visa prislista
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => openDeleteDialog(supplier)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Ta bort
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Ta bort leverantör
            </DialogTitle>
            <DialogDescription className="pt-2">
              Är du säker på att du vill ta bort <strong>{deleteDialog.supplier?.company_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          {deleteDialog.stats && (deleteDialog.stats.prices_count > 0 || deleteDialog.stats.orders_count > 0) && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 my-2">
              <p className="font-medium text-destructive mb-2">⚠️ Varning! Detta kommer även ta bort:</p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                {deleteDialog.stats.prices_count > 0 && (
                  <li>• <strong>{deleteDialog.stats.prices_count}</strong> prisrader</li>
                )}
                {deleteDialog.stats.orders_count > 0 && (
                  <li>• <strong>{deleteDialog.stats.orders_count}</strong> inköpsordrar</li>
                )}
              </ul>
              <p className="text-xs text-muted-foreground mt-3">
                Denna åtgärd kan inte ångras.
              </p>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, supplier: null, stats: null })}>
              Avbryt
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(deleteDialog.stats?.prices_count > 0 || deleteDialog.stats?.orders_count > 0)} 
              disabled={deleting}
            >
              {deleting ? "Tar bort..." : "Ta bort allt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
